import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Divider, MenuItem,
  Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import {
  previewBlofinManualOrder, prepareBlofinManualOrder, fetchBlofinManualOrder,
} from '../../api/manualTrading';
import BlofinLiveOrderPanel from './BlofinLiveOrderPanel';

const INITIAL = {
  demoAccountIdentityRef: '',
  instrumentId: 'BTC-USDT',
  direction: 'LONG',
  orderType: 'MARKET',
  marginUsdt: '',
  leverage: '',
  limitPrice: '',
  stopLoss: '',
  tp1: '',
  tp2: '',
  tp3: '',
};

function messageFor(error) {
  const status = error?.status;
  if (status === 400) return 'RequÃƒÂªte invalide (champs ou prix incohÃƒÂ©rents).';
  if (status === 403) return 'AccÃƒÂ¨s au compte Demo refusÃƒÂ©.';
  if (status === 404) return 'PrÃƒÂ©paration introuvable.';
  if (status === 409) return 'Cette clÃƒÂ© a dÃƒÂ©jÃƒÂ  ÃƒÂ©tÃƒÂ© utilisÃƒÂ©e avec des paramÃƒÂ¨tres diffÃƒÂ©rents.';
  if (status === 503) return 'DonnÃƒÂ©es de marchÃƒÂ© indisponibles, rÃƒÂ©essayez plus tard.';
  return error?.message || 'Erreur inattendue.';
}

function Summary({ data }) {
  if (!data) return null;
  const rows = [
    ['Statut', data.status],
    ['Instrument', data.instrumentId],
    ['Direction', data.direction],
    ['Type', data.orderType],
    ['Marge USDT', data.marginUsdt],
    ['Levier', data.leverage],
    ['Notionnel', data.targetNotionalUsdt],
    ['Prix de rÃƒÂ©fÃƒÂ©rence', data.entryPrice],
    ['Contrats', data.totalContracts],
    ['Stop-loss', data.stopLoss],
    ['TP1', `${data.tp1} (${data.tp1Contracts})`],
    ['TP2', `${data.tp2} (${data.tp2Contracts})`],
    ['TP3', `${data.tp3} (${data.tp3Contracts})`],
    ['Runner', data.runnerContracts],
  ];
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6">RÃƒÂ©capitulatif</Typography>
        <Divider sx={{ my: 1 }} />
        <Stack spacing={0.5}>
          {rows.map(([label, value]) => (
            <Box key={label} display="flex" justifyContent="space-between" gap={2}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography variant="body2">{value ?? '-'}</Typography>
            </Box>
          ))}
        </Stack>
        {data.referenceKind === 'MARKET_REFERENCE' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Le prix MARKET est indicatif ; il ne garantit pas le prix d'exÃƒÂ©cution.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default function BlofinManualOrderPage() {
  const [mode, setMode] = useState('DEMO');
  const [form, setForm] = useState(INITIAL);
  const [preview, setPreview] = useState(null);
  const [saved, setSaved] = useState(null);
  const [lookupId, setLookupId] = useState('');
  const [lookedUp, setLookedUp] = useState(null);
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [previewSignature, setPreviewSignature] = useState('');
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');

  const signature = useMemo(() => JSON.stringify(form), [form]);
  const previewStale = preview !== null && signature !== previewSignature;
  const isLimit = form.orderType === 'LIMIT';

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    setError('');
    setSaved(null);
  }

  function body() {
    const payload = {
      ...form,
      marginUsdt: form.marginUsdt,
      leverage: form.leverage,
      stopLoss: form.stopLoss,
      tp1: form.tp1,
      tp2: form.tp2,
      tp3: form.tp3,
    };
    if (isLimit) payload.limitPrice = form.limitPrice;
    else delete payload.limitPrice;
    return payload;
  }

  async function onPreview() {
    setLoading('preview');
    setError('');
    try {
      const data = await previewBlofinManualOrder(body());
      setPreview(data);
      setPreviewSignature(signature);
      if (!idempotencyKey) setIdempotencyKey(crypto.randomUUID());
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setLoading('');
    }
  }

  async function onPrepare() {
    if (!idempotencyKey) setIdempotencyKey(crypto.randomUUID());
    setLoading('prepare');
    setError('');
    try {
      const data = await prepareBlofinManualOrder(body(), idempotencyKey || crypto.randomUUID());
      setSaved(data);
      setPreview(data);
      setPreviewSignature(signature);
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setLoading('');
    }
  }

  async function onLookup() {
    setLoading('lookup');
    setError('');
    try {
      setLookedUp(await fetchBlofinManualOrder(lookupId.trim()));
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setLoading('');
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>Ordres BloFin</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Préparation DEMO et préparation LIVE verrouillée. Aucun envoi d'ordre depuis cette page.
      </Typography>
      <Tabs value={mode} onChange={(_, value) => setMode(value)} sx={{ mt: 1, mb: 1 }}>
        <Tab value="DEMO" label="DEMO" />
        <Tab value="LIVE" label="LIVE" />
      </Tabs>

      {mode === 'DEMO' ? (
        <>
          {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} sx={{ mt: 2 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Stack spacing={2}>
              <TextField label="Compte Demo (identitÃƒÂ© technique)" value={form.demoAccountIdentityRef}
                onChange={(e) => update('demoAccountIdentityRef', e.target.value)} required fullWidth />
              <TextField label="Instrument" id="instrument" value={form.instrumentId}
                onChange={(e) => update('instrumentId', e.target.value)} required />
              <Stack direction="row" spacing={2}>
                <TextField select label="Direction" id="direction" value={form.direction}
                  onChange={(e) => update('direction', e.target.value)} fullWidth>
                  <MenuItem value="LONG">LONG</MenuItem>
                  <MenuItem value="SHORT">SHORT</MenuItem>
                </TextField>
                <TextField select label="Type" id="order-type" value={form.orderType}
                  onChange={(e) => update('orderType', e.target.value)} fullWidth>
                  <MenuItem value="MARKET">MARKET</MenuItem>
                  <MenuItem value="LIMIT">LIMIT</MenuItem>
                </TextField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField label="Marge USDT" id="margin" type="number" value={form.marginUsdt}
                  onChange={(e) => update('marginUsdt', e.target.value)} required fullWidth />
                <TextField label="Levier" id="leverage" type="number" value={form.leverage}
                  onChange={(e) => update('leverage', e.target.value)} required fullWidth />
              </Stack>
              {isLimit && (
                <TextField label="Prix LIMIT" id="limit-price" type="number" value={form.limitPrice}
                  onChange={(e) => update('limitPrice', e.target.value)} required />
              )}
              <Stack direction="row" spacing={2}>
                <TextField label="Stop-loss" id="stop-loss" type="number" value={form.stopLoss}
                  onChange={(e) => update('stopLoss', e.target.value)} required fullWidth />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField label="TP1" id="tp1" type="number" value={form.tp1}
                  onChange={(e) => update('tp1', e.target.value)} required fullWidth />
                <TextField label="TP2" id="tp2" type="number" value={form.tp2}
                  onChange={(e) => update('tp2', e.target.value)} required fullWidth />
                <TextField label="TP3" id="tp3" type="number" value={form.tp3}
                  onChange={(e) => update('tp3', e.target.value)} required fullWidth />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={onPreview}
                  disabled={loading !== '' || previewStale && false}>
                  {loading === 'preview' ? <CircularProgress size={18} /> : 'PrÃƒÂ©visualiser'}
                </Button>
                <Button variant="contained" onClick={onPrepare}
                  disabled={loading !== '' || !preview || previewStale}>
                  {loading === 'prepare' ? <CircularProgress size={18} /> : 'Enregistrer la prÃƒÂ©paration'}
                </Button>
              </Stack>
              {previewStale && (
                <Alert severity="warning">
                  Les paramÃƒÂ¨tres ont changÃƒÂ© : relancez une prÃƒÂ©visualisation avant d'enregistrer.
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Stack spacing={2} sx={{ flex: 1 }}>
          <Summary data={saved || preview} />
          {saved && (
            <Alert severity="success">
              Préparation {saved.created ? 'créée' : 'réutilisée'} — strategyId {saved.strategyId}
            </Alert>
          )}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6">Consulter une prÃƒÂ©paration</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField size="small" fullWidth label="strategyId" id="strategy-id" value={lookupId}
                  onChange={(e) => setLookupId(e.target.value)} />
                <Button onClick={onLookup} disabled={loading !== '' || !lookupId.trim()}>
                  {loading === 'lookup' ? <CircularProgress size={18} /> : 'Consulter'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          {lookedUp && <Summary data={lookedUp} />}
        </Stack>
          </Stack>
        </>
      ) : (
        <BlofinLiveOrderPanel />
      )}
    </Box>
  );
}
